import axios from 'axios';
import { appConfig } from '../config/app.config';
import feathersClient from '../feathers';

const API_URL = `${appConfig.API_ENDPOINT}`;

export interface Organization {
    _id: string;
    name: string;
    slug: string; // Stytch-generated slug
    customSlug?: string; // User-editable custom slug
    logoUrl?: string; // GCS URL for company logo
    stytchOrganizationId: string;
    settings?: {
        allowedEmailDomains?: string[];
        ssoEnabled?: boolean;
    };
}

export interface OrganizationMember {
    _id: string;
    email: string;
    displayName?: string;
    organizationRole?: 'owner' | 'admin' | 'member';
    profilePicture?: string;
}

export interface UpdateOrganizationData {
    name?: string;
    customSlug?: string;
    settings?: {
        allowedEmailDomains?: string[];
        ssoEnabled?: boolean;
    };
}

class OrganizationService {
    /**
     * Get current user's organization
     */
    async getCurrentOrganization(): Promise<Organization> {
        const response = await axios.get(`${API_URL}/organization-management/current`, {
            headers: {
                Authorization: `Bearer ${this.getToken()}`
            }
        });
        return response.data.organization;
    }

    /**
     * Update organization settings (admin only)
     */
    async updateOrganization(data: UpdateOrganizationData): Promise<Organization> {
        const response = await axios.patch(`${API_URL}/organization-management/current`, data, {
            headers: {
                Authorization: `Bearer ${this.getToken()}`
            }
        });
        return response.data.organization;
    }

    /**
     * Get organization members
     */
    async getMembers(): Promise<OrganizationMember[]> {
        const response = await axios.get(`${API_URL}/organization-management/members`, {
            headers: {
                Authorization: `Bearer ${this.getToken()}`
            }
        });
        return response.data.members;
    }

    /**
     * Update member role (admin only)
     */
    async updateMemberRole(memberId: string, organizationRole: 'owner' | 'admin' | 'member'): Promise<OrganizationMember> {
        const response = await axios.patch(
            `${API_URL}/organization-management/members/${memberId}`,
            { organizationRole },
            {
                headers: {
                    Authorization: `Bearer ${this.getToken()}`
                }
            }
        );
        return response.data.member;
    }

    /**
     * Remove member from organization (admin only)
     */
    async removeMember(memberId: string): Promise<void> {
        await axios.delete(`${API_URL}/organization-management/members/${memberId}`, {
            headers: {
                Authorization: `Bearer ${this.getToken()}`
            }
        });
    }

    /**
     * Invite new member (admin only)
     * Note: Currently requires Stytch dashboard configuration
     */
    async inviteMember(email: string): Promise<any> {
        const response = await axios.post(
            `${API_URL}/organization-management/invite`,
            { email },
            {
                headers: {
                    Authorization: `Bearer ${this.getToken()}`
                }
            }
        );
        return response.data;
    }

    /**
     * Switch to a different organization
     */
    async switchOrganization(organizationId: string, intermediateSessionToken?: string, sessionToken?: string): Promise<any> {
        const response = await axios.post(
            `${API_URL}/auth/organization/switch`,
            {
                organizationId,
                intermediateSessionToken,
                sessionToken
            }
        );
        return response.data;
    }


    /**
     * Discover all organizations the user belongs to (lazy-loaded)
     */
    async discoverOrganizations(): Promise<{ discoveredOrganizations: any[]; intermediateSessionToken: string | null }> {
        const response = await axios.get(`${API_URL}/organization-discovery/list`, {
            headers: {
                Authorization: `Bearer ${this.getToken()}`
            }
        });
        return response.data;
    }

    /**
     * Upload organization logo (admin only)
     */
    async uploadLogo(file: File): Promise<Organization> {
        const formData = new FormData();
        formData.append('logo', file);

        const response = await axios.post(
            `${API_URL}/organization-management/logo`,
            formData,
            {
                headers: {
                    Authorization: `Bearer ${this.getToken()}`,
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response.data.organization;
    }

    /**
     * Delete organization logo (admin only)
     */
    async deleteLogo(): Promise<Organization> {
        const response = await axios.delete(`${API_URL}/organization-management/logo`, {
            headers: {
                Authorization: `Bearer ${this.getToken()}`
            }
        });
        return response.data.organization;
    }

    /**
     * Get current auth token
     */
    private getToken(): string | null {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            return user.accessToken || null;
        }
        return null;
    }
}

export default new OrganizationService();
